import React, { useState, useEffect } from "react";
import axios from "../services/httpClient";
import { toast } from "react-toastify";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

const boards = ["CBSE", "ICSE", "IB", "IGCSE", "Cambridge"];
const grades = Array.from({ length: 12 }, (_, i) => i + 1);

// Color mapping for subjects
const getSubjectColor = (subject) => {
  const colors = {
    Mathematics: "bg-blue-100 text-blue-800 border-blue-200",
    Science: "bg-green-100 text-green-800 border-green-200",
    Physics: "bg-red-100 text-red-800 border-red-200",
    Chemistry: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Biology: "bg-emerald-100 text-emerald-800 border-emerald-200",
    English: "bg-purple-100 text-purple-800 border-purple-200",
  };
  return colors[subject] || "bg-gray-100 text-gray-800 border-gray-200";
};

const AdminQBSummary = () => {
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState(new Set());

  // Fetch analytics when board and grade are selected
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!board || !grade) {
        setAnalyticsData(null);
        setExpandedChapters(new Set());
        return;
      }

      setLoading(true);
      // Reset expanded chapters when filters change
      setExpandedChapters(new Set());

      try {
        const params = new URLSearchParams({
          board: board,
          grade: grade.toString(),
        });

        const response = await axios.get(
          `/v2/question-pool/analytics?${params.toString()}`
        );

        if (response.data && response.data.success) {
          setAnalyticsData(response.data);
        } else {
          setAnalyticsData(null);
          toast.error("Failed to load analytics data");
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
        toast.error("Failed to load question bank summary. Please try again.");
        setAnalyticsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [board, grade]);

  // Calculate total questions across all subjects
  const getTotalQuestions = () => {
    if (!analyticsData || !analyticsData.subjects) return 0;
    return analyticsData.subjects.reduce((total, subject) => {
      return (
        total +
        subject.chapters.reduce(
          (sum, chapter) => sum + (chapter.question_count || 0),
          0
        )
      );
    }, 0);
  };

  // Toggle chapter expansion
  const toggleChapter = (subjectIndex, chapterIndex) => {
    const key = `${subjectIndex}-${chapterIndex}`;
    setExpandedChapters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Check if chapter is expanded
  const isChapterExpanded = (subjectIndex, chapterIndex) => {
    return expandedChapters.has(`${subjectIndex}-${chapterIndex}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800">QB Summary</h1>
          {analyticsData && (
            <div className="text-lg font-semibold text-gray-700">
              Total Questions:{" "}
              <span className="text-primary">
                {getTotalQuestions().toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-6 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Board
              </label>
              <select
                value={board}
                onChange={(e) => setBoard(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="">Select Board</option>
                {boards.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grade
              </label>
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="">Select Grade</option>
                {grades.map((g) => (
                  <option key={g} value={g}>
                    Grade {g}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-500">
              Loading question bank summary...
            </p>
          </div>
        )}

        {/* Empty State - No filters selected */}
        {!loading && !board && !grade && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              Please select Board and Grade to view question bank summary
            </p>
          </div>
        )}

        {/* Empty State - No data */}
        {!loading &&
          board &&
          grade &&
          analyticsData &&
          !analyticsData.subjects?.length && (
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
              <p className="text-gray-500 text-lg">No data available</p>
              <p className="text-gray-400 text-sm mt-2">
                No questions found for {board} Grade {grade}
              </p>
            </div>
          )}

        {/* Subject Cards */}
        {!loading &&
          analyticsData &&
          analyticsData.subjects &&
          analyticsData.subjects.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {analyticsData.subjects.map((subject, subjectIndex) => {
                const subjectTotal = subject.chapters.reduce(
                  (sum, chapter) => sum + (chapter.question_count || 0),
                  0
                );

                return (
                  <div
                    key={subjectIndex}
                    className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden flex flex-col"
                    style={{ maxHeight: "600px" }}
                  >
                    {/* Subject Header */}
                    <div
                      className={`px-6 py-4 border-b-2 ${getSubjectColor(
                        subject.subject
                      )}`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold">{subject.subject}</h3>
                        <span className="px-3 py-1 bg-white/80 rounded-full text-sm font-semibold">
                          {subjectTotal} Questions
                        </span>
                      </div>
                      <p className="text-sm mt-1 opacity-75">
                        {subject.chapters.length} Chapters
                      </p>
                    </div>

                    {/* Chapters List - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <div className="space-y-2">
                        {subject.chapters.map((chapter, chapterIndex) => {
                          const isExpanded = isChapterExpanded(
                            subjectIndex,
                            chapterIndex
                          );
                          const hasTopics =
                            chapter.topics && chapter.topics.length > 0;

                          return (
                            <div key={chapterIndex} className="space-y-2">
                              {/* Chapter Row - Clickable */}
                              <div
                                onClick={() =>
                                  hasTopics &&
                                  toggleChapter(subjectIndex, chapterIndex)
                                }
                                className={`flex items-center justify-between p-3 rounded-lg transition-colors border ${
                                  hasTopics
                                    ? "bg-gray-50 hover:bg-gray-100 cursor-pointer border-gray-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  {hasTopics && (
                                    <div className="flex-shrink-0 text-gray-400">
                                      {isExpanded ? (
                                        <FaChevronDown className="w-3 h-3" />
                                      ) : (
                                        <FaChevronRight className="w-3 h-3" />
                                      )}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">
                                      {chapter.chapter}
                                    </p>
                                  </div>
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                                    {chapter.question_count || 0}
                                  </span>
                                </div>
                              </div>

                              {/* Topics List - Expanded */}
                              {isExpanded && hasTopics && (
                                <div className="ml-6 mt-1 space-y-1.5 border-l-2 border-gray-200 pl-4">
                                  {chapter.topics.map((topic, topicIndex) => (
                                    <div
                                      key={topicIndex}
                                      className="flex items-center justify-between p-2.5 bg-blue-50/50 rounded-md hover:bg-blue-50 transition-colors border border-blue-100"
                                    >
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-700 truncate">
                                          {topic.topic}
                                        </p>
                                      </div>
                                      <div className="ml-3 flex-shrink-0">
                                        <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                          {topic.question_count || 0}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
};

export default AdminQBSummary;
